import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

const SocialLoginOptions = () => {
  const navigate = useNavigate();
  const { signInWithGoogle } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [error, setError] = useState('');

  const handleGoogleLogin = async () => {
    setLoadingProvider('google');
    setError('');
    
    try {
      const { error } = await signInWithGoogle();
      
      if (error) {
        setError(error?.message);
        setLoadingProvider(null);
      }
      // Note: Redirect happens automatically via Supabase OAuth flow
    } catch (err) {
      setError('Failed to connect to Google. Please try again.');
      setLoadingProvider(null);
    }
  };

  const handleOtherProviders = (providerId) => {
    setError(`${providerId} authentication is not configured yet. Please use email/password login or Google.`);
  };

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
        </div>
      </div>

      {/* Social Login Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Button
          variant="outline"
          onClick={handleGoogleLogin}
          loading={loadingProvider === 'google'}
          disabled={loadingProvider !== null}
          className="flex items-center justify-center space-x-2 hover:bg-red-50 transition-colors"
        >
          <Icon 
            name="Chrome" 
            size={18} 
            className="text-red-600"
          />
          <span className="hidden sm:inline">Google</span>
        </Button>

        <Button
          variant="outline"
          onClick={() => handleOtherProviders('Microsoft')}
          disabled={loadingProvider !== null}
          className="flex items-center justify-center space-x-2 hover:bg-blue-50 transition-colors"
        >
          <Icon 
            name="Square" 
            size={18} 
            className="text-blue-600"
          />
          <span className="hidden sm:inline">Microsoft</span>
        </Button>

        <Button
          variant="outline"
          onClick={() => handleOtherProviders('Apple')}
          disabled={loadingProvider !== null}
          className="flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors"
        >
          <Icon 
            name="Smartphone" 
            size={18} 
            className="text-gray-800"
          />
          <span className="hidden sm:inline">Apple</span>
        </Button>
      </div>

      {/* Alternative Login Methods */}
      <div className="space-y-2">
        <Button
          variant="ghost"
          onClick={() => handleOtherProviders('Phone')}
          disabled={loadingProvider !== null}
          className="w-full flex items-center justify-center space-x-2"
        >
          <Icon name="Phone" size={18} />
          <span>Continue with Phone Number</span>
        </Button>

        <Button
          variant="ghost"
          onClick={() => handleOtherProviders('Enterprise SSO')}
          disabled={loadingProvider !== null}
          className="w-full flex items-center justify-center space-x-2"
        >
          <Icon name="Building" size={18} />
          <span>Enterprise SSO Login</span>
        </Button>
      </div>

      {/* Security Notice */}
      <div className="mt-6 p-3 bg-muted/50 rounded-md">
        <div className="flex items-start space-x-2">
          <Icon name="Shield" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            Your data is protected with enterprise-grade security. We never store your social login credentials.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SocialLoginOptions;